import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { PaymentDetailsEntry } from '@/modules/settings/types/payment-details';

import { UpdateInvoiceFormValues } from '../../validations';

interface InvoicePaymentDetailsSelectProps {
  form: UseFormReturn<UpdateInvoiceFormValues>;
  entries: PaymentDetailsEntry[];
  isLoading: boolean;
}

/** Selects an optional payment-details entry while keeping the form value nullable. */
export function InvoicePaymentDetailsSelect({
  form,
  entries,
  isLoading,
}: InvoicePaymentDetailsSelectProps) {
  const { t } = useTranslation('authenticated.invoices');
  const noneOption = {
    value: 'none',
    label: t('details.edit.paymentDetails.none'),
  };
  const options = [
    noneOption,
    ...entries.map(entry => ({ value: entry.id.toString(), label: entry.name })),
  ];
  const paymentDetailsId = form.watch('paymentDetailsId');
  const selectedOption =
    options.find(option => option.value === (paymentDetailsId?.toString() ?? noneOption.value)) ??
    noneOption;

  return (
    <SingleSelect
      control={form.control}
      name="paymentDetailsId"
      label={<span className="sr-only">{t('details.edit.placeholders.paymentDetails')}</span>}
      placeholder={t('details.edit.placeholders.paymentDetails')}
      options={options}
      selectedOption={selectedOption}
      disabled={isLoading}
      onChange={value =>
        form.setValue('paymentDetailsId', value === noneOption.value ? null : Number(value), {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    />
  );
}
